import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SwapComponent from './SwapComponent';
import TransactionHistory from './TransactionHistory';
import SimpleLiquidityPool from './SimpleLiquidityPool';
import LanguageSwitcher from './LanguageSwitcher';
import useSwap from './useSwap';
import './App.css';

function App() {
  // Note: explicitly include 'common' namespace here
  const { t, i18n } = useTranslation(['common']);
  const [activeTab, setActiveTab] = useState('swap');
  const [transactions, setTransactions] = useState([]);
  // Prefix unused variables with underscore
  const { account, connectWallet, logout } = useSwap();
  const [componentKey, setComponentKey] = useState(Date.now());
  const [showSettings, setShowSettings] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'auto');

  const normalizedAccount = account ? account.toLowerCase() : null;

  // Log current translation state on mount for debugging
  useEffect(() => {
    console.log("Current language:", i18n.language);
    console.log("Available namespaces:", i18n.options.ns);
    console.log("App name translation:", t('app.name'));
  }, [i18n, t]);

  // Update theme based on state and system preference
  useEffect(() => {
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const effectiveTheme = theme === 'auto' ? (systemPrefersDark ? 'dark' : 'light') : theme;

    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(`${effectiveTheme}-theme`);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    window.addTransaction = (transaction) => {
      if (!normalizedAccount) return;
      try {
        const storageKey = `dex_transactions_${normalizedAccount}`;
        let existingTransactions = [];
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            if (Array.isArray(parsed)) {
              existingTransactions = parsed;
            }
          } catch (e) {
            console.error("Error parsing existing transactions:", e);
          }
        }
        const newTransactions = [transaction, ...existingTransactions];
        localStorage.setItem(storageKey, JSON.stringify(newTransactions));
        setTransactions(newTransactions);
      } catch (error) {
        console.error("Error saving transaction:", error);
      }
    };
    return () => {
      window.addTransaction = null;
    };
  }, [normalizedAccount]);

  useEffect(() => {
    if (!normalizedAccount) {
      setTransactions([]);
      return;
    }
    try {
      const storageKey = `dex_transactions_${normalizedAccount}`;
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (Array.isArray(parsedData)) {
            setTransactions(parsedData);
            return;
          }
        } catch (e) {
          console.error("App: Error parsing transactions:", e);
        }
      }
      const allKeys = Object.keys(localStorage);
      const matchingKeys = allKeys.filter(key =>
        key.toLowerCase().includes(normalizedAccount)
      );
      for (const key of matchingKeys) {
        try {
          const altData = localStorage.getItem(key);
          if (altData) {
            const altParsed = JSON.parse(altData);
            if (Array.isArray(altParsed) && altParsed.length > 0) {
              setTransactions(altParsed);
              localStorage.setItem(storageKey, altData);
              return;
            }
          }
        } catch (innerError) {
          console.error(`Error checking alternative key ${key}:`, innerError);
        }
      }
      setTransactions([]);
    } catch (error) {
      console.error("App: Error loading transactions:", error);
      setTransactions([]);
    }
  }, [normalizedAccount]);

  const handleConnectWallet = async () => {
    await connectWallet();
  };

  const handleLogout = () => {
    if (logout) {
      logout();
      setComponentKey(Date.now());
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.settings-menu-wrapper')) {
        setShowSettings(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="App">
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="12" fill="url(#gradient)" />
              <path d="M14 13.5V20C14 23.5899 16.9101 26.5 20.5 26.5C24.0899 26.5 27 23.5899 27 20V13.5" stroke="white" strokeWidth="3" strokeLinecap="round" />
              <path d="M10 18H14M27 18H31" stroke="white" strokeWidth="3" strokeLinecap="round" />
              <circle cx="20.5" cy="20" r="3" fill="white" />
              <defs>
                <linearGradient id="gradient" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#4c6ef5" />
                  <stop offset="1" stopColor="#5f3dc4" />
                </linearGradient>
              </defs>
            </svg>
            <span className="logo-text">{t('app.name')}</span>
          </div>
          <nav className="header-nav">
            {/* Fix empty href warnings by using buttons instead of empty links */}
            <button className="nav-link-button">{t('navigation.docs')}</button>
            <button className="nav-link-button">{t('navigation.about')}</button>
            <LanguageSwitcher />
            {account ? (
              <div className="wallet-buttons">
                <button className="connect-header-btn connected">
                  {account.substring(0, 6)}...{account.substring(account.length - 4)}
                </button>
                <div className="settings-menu-wrapper">
                  <button className="settings-icon-button" onClick={() => setShowSettings(prev => !prev)}>
                    {/* Fix emoji accessibility */}
                    <span role="img" aria-label="Settings">⚙️</span>
                  </button>
                  {showSettings && (
                    <div className="settings-dropdown">
                      <div className="theme-toggle-row">
                        <span className="theme-label">{t('settings.theme')}</span>
                        <div className="theme-toggle">
                          <button className={theme === 'auto' ? 'selected' : ''} onClick={() => setTheme('auto')}>
                            {t('settings.auto')}
                          </button>
                          <button className={theme === 'light' ? 'selected' : ''} onClick={() => setTheme('light')}>
                            {/* Fix emoji accessibility */}
                            <span role="img" aria-label="Light Theme">☀️</span>
                          </button>
                          <button className={theme === 'dark' ? 'selected' : ''} onClick={() => setTheme('dark')}>
                            {/* Fix emoji accessibility */}
                            <span role="img" aria-label="Dark Theme">🌙</span>
                          </button>
                        </div>
                      </div>
                      <button onClick={() => alert(t('settings.comingSoon'))}>{t('settings.network')}</button>
                      <button onClick={handleLogout}>{t('buttons.disconnect')}</button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button className="connect-header-btn" onClick={handleConnectWallet}>
                {t('buttons.connectHeader')}
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className="content-wrapper">
        <div className="tabs">
          <button 
            className={activeTab === 'swap' ? 'active' : ''}
            onClick={() => setActiveTab('swap')}
          >
            {t('navigation.swap')}
          </button>
          <button 
            className={activeTab === 'liquidity' ? 'active' : ''}
            onClick={() => setActiveTab('liquidity')}
          >
            {t('navigation.liquidity')}
          </button>
          <button 
            className={activeTab === 'history' ? 'active' : ''}
            onClick={() => setActiveTab('history')}
          >
            {t('navigation.history')}
          </button>
        </div>

        {activeTab === 'swap' && (
          <SwapComponent account={account} connectWallet={handleConnectWallet} key={`swap-${componentKey}-${normalizedAccount || 'no-wallet'}`} />
        )}
        {activeTab === 'liquidity' && (
          <SimpleLiquidityPool account={account} key={`liquidity-${componentKey}-${normalizedAccount || 'no-wallet'}`} />
        )}
        {activeTab === 'history' && (
          <TransactionHistory transactions={transactions} setTransactions={setTransactions} account={account} key={`history-${componentKey}-${normalizedAccount || 'no-wallet'}`} />
        )}
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>{t('app.name')}</h3>
            <p>{t('app.tagline')}</p>
          </div>
          <div className="footer-section">
            <h4>{t('footer.resources')}</h4>
            <ul>
              {/* Replace empty href links with buttons */}
              <li><button className="footer-link">{t('footer.documentation')}</button></li>
              <li><button className="footer-link">{t('footer.github')}</button></li>
              <li><button className="footer-link">{t('footer.support')}</button></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.community')}</h4>
            <ul>
              <li><button className="footer-link">{t('footer.twitter')}</button></li>
              <li><button className="footer-link">{t('footer.discord')}</button></li>
              <li><button className="footer-link">{t('footer.telegram')}</button></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>{t('footer.legal')}</h4>
            <ul>
              <li><button className="footer-link">{t('footer.terms')}</button></li>
              <li><button className="footer-link">{t('footer.privacy')}</button></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>{t('footer.copyright')}</p>
        </div>
      </footer>
    </div>
  );
}

export default App;